/**
 * All game audio, synthesized at runtime with the Web Audio API.
 *
 * There are no sound files. Every stomp, siren, footstep and breath is
 * generated from oscillators and filtered noise. That is partly practical —
 * zero assets to source or license — but mostly it is control: the monster's
 * distance can drive filter cutoff, his alertness can drive siren pitch, and
 * the player's stamina can drive breathing rate, continuously and per-frame.
 * Pre-recorded samples cannot do that.
 *
 * The distance model matters more than the timbre. A stomp forty metres away
 * is not simply a quieter stomp — it has lost its high frequencies to the air
 * and gained a tail off the trees. Getting that right is what makes players
 * able to tell how far away he is, which is the entire skill of the game.
 */

import { CueKind, VOICE } from '@static-pines/shared';

/**
 * A persistent spatialised input for a teammate's voice.
 *
 * One-shot cues get a fresh `spatial()` channel per sound; a voice is a
 * continuous stream, so it keeps one channel alive and drags it through the
 * world as its owner moves.
 */
export interface VoiceChannel {
  input: GainNode;
  setPosition(x: number, y: number, z: number): void;
  dispose(): void;
}

export class AudioEngine {
  private ctx: AudioContext | null = null;

  private master!: GainNode;
  private reverb!: ConvolverNode;
  private reverbSend!: GainNode;
  private compressor!: DynamicsCompressorNode;

  // Continuous layers
  private windGain!: GainNode;
  private heartGain!: GainNode;
  private breathGain!: GainNode;

  private heartTimer = 0;
  private heartRate = 0.9;
  private breathTimer = 0;
  private breathRate = 4;

  private dread = 0;
  private exertion = 0;
  private started = false;
  private muted = false;

  /** Position of the listener, kept for manual distance calculations. */
  private listenerPos = { x: 0, y: 0, z: 0 };

  // -------------------------------------------------------------------------
  // Setup
  // -------------------------------------------------------------------------

  /** Must be called from a user gesture — browsers refuse audio otherwise. */
  async start(): Promise<void> {
    if (this.started) {
      if (this.ctx?.state === 'suspended') await this.ctx.resume();
      return;
    }
    this.started = true;

    const ctx = new AudioContext({ latencyHint: 'interactive' });
    this.ctx = ctx;

    this.compressor = ctx.createDynamicsCompressor();
    this.compressor.threshold.value = -18;
    this.compressor.knee.value = 24;
    this.compressor.ratio.value = 8;
    this.compressor.attack.value = 0.004;
    this.compressor.release.value = 0.22;
    this.compressor.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.gain.value = 0.9;
    this.master.connect(this.compressor);

    this.reverb = ctx.createConvolver();
    this.reverb.buffer = this.buildForestImpulse(2.8, 2.6);
    this.reverbSend = ctx.createGain();
    this.reverbSend.gain.value = 1;
    this.reverbSend.connect(this.reverb);
    this.reverb.connect(this.master);

    this.buildWind();
    this.buildBodyLayers();

    if (ctx.state === 'suspended') await ctx.resume();
  }

  get isRunning(): boolean {
    return this.ctx !== null && this.ctx.state === 'running';
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.master) this.master.gain.value = muted ? 0 : 0.9;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /**
   * A synthetic impulse response: exponentially decaying noise, darkened over
   * time. Approximates a large open space with soft, absorbent boundaries —
   * which is what a forest is, acoustically.
   */
  private buildForestImpulse(seconds: number, decay: number): AudioBuffer {
    const ctx = this.ctx!;
    const rate = ctx.sampleRate;
    const length = Math.floor(rate * seconds);
    const buffer = ctx.createBuffer(2, length, rate);

    for (let channel = 0; channel < 2; channel++) {
      const data = buffer.getChannelData(channel);
      let lowpassState = 0;

      for (let i = 0; i < length; i++) {
        const t = i / length;
        const envelope = Math.pow(1 - t, decay);
        const white = Math.random() * 2 - 1;

        // A one-pole lowpass that closes as the tail decays, so late
        // reflections are darker than early ones.
        const coefficient = 0.28 - t * 0.2;
        lowpassState += coefficient * (white - lowpassState);

        // Sparse early reflections give the tail a sense of scale.
        const sparkle = i < rate * 0.12 && Math.random() < 0.0016 ? 3.2 : 1;
        data[i] = lowpassState * envelope * sparkle;
      }
    }
    return buffer;
  }

  private noiseBuffer(seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const length = Math.floor(ctx.sampleRate * seconds);
    const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
    return buffer;
  }

  // -------------------------------------------------------------------------
  // Continuous layers
  // -------------------------------------------------------------------------

  private buildWind(): void {
    const ctx = this.ctx!;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(6);
    source.loop = true;

    // Two filters: a low rumble bed and a moving whistle through the branches.
    const bed = ctx.createBiquadFilter();
    bed.type = 'lowpass';
    bed.frequency.value = 340;

    const whistle = ctx.createBiquadFilter();
    whistle.type = 'bandpass';
    whistle.frequency.value = 900;
    whistle.Q.value = 1.6;

    const whistleGain = ctx.createGain();
    whistleGain.gain.value = 0.16;

    this.windGain = ctx.createGain();
    this.windGain.gain.value = 0.09;

    source.connect(bed).connect(this.windGain);
    source.connect(whistle).connect(whistleGain).connect(this.windGain);
    this.windGain.connect(this.master);

    // Slow gusting — an LFO on both cutoff and level.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.055;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 260;
    lfo.connect(lfoGain).connect(whistle.frequency);
    lfo.start();

    const gustLfo = ctx.createOscillator();
    gustLfo.frequency.value = 0.031;
    const gustGain = ctx.createGain();
    gustGain.gain.value = 0.045;
    gustLfo.connect(gustGain).connect(this.windGain.gain);
    gustLfo.start();

    source.start();
  }

  private buildBodyLayers(): void {
    const ctx = this.ctx!;

    this.heartGain = ctx.createGain();
    this.heartGain.gain.value = 0;
    this.heartGain.connect(this.master);

    this.breathGain = ctx.createGain();
    this.breathGain.gain.value = 0;
    this.breathGain.connect(this.master);
  }

  // -------------------------------------------------------------------------
  // Per-frame
  // -------------------------------------------------------------------------

  /**
   * @param dread    0..1, monster proximity and awareness
   * @param exertion 0..1, how hard the player is breathing
   */
  update(
    dt: number,
    listener: { x: number; y: number; z: number },
    forward: { x: number; y: number; z: number },
    up: { x: number; y: number; z: number },
    dread: number,
    exertion: number,
  ): void {
    if (!this.ctx) return;
    const ctx = this.ctx;

    this.listenerPos = listener;
    this.dread = dread;
    this.exertion = exertion;

    const l = ctx.listener;
    const t = ctx.currentTime;

    // Modern AudioParam interface where available; older setters otherwise.
    if (l.positionX) {
      l.positionX.setValueAtTime(listener.x, t);
      l.positionY.setValueAtTime(listener.y, t);
      l.positionZ.setValueAtTime(listener.z, t);
      l.forwardX.setValueAtTime(forward.x, t);
      l.forwardY.setValueAtTime(forward.y, t);
      l.forwardZ.setValueAtTime(forward.z, t);
      l.upX.setValueAtTime(up.x, t);
      l.upY.setValueAtTime(up.y, t);
      l.upZ.setValueAtTime(up.z, t);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      (l as any).setPosition(listener.x, listener.y, listener.z);
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      (l as any).setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }

    this.updateHeartbeat(dt);
    this.updateBreathing(dt);

    // Wind ducks slightly as dread rises, clearing space for the monster.
    this.windGain.gain.setTargetAtTime(0.09 * (1 - dread * 0.45), t, 0.5);
  }

  private updateHeartbeat(dt: number): void {
    if (this.dread < 0.12) {
      this.heartGain.gain.setTargetAtTime(0, this.ctx!.currentTime, 0.4);
      return;
    }

    this.heartGain.gain.setTargetAtTime(0.5 * this.dread, this.ctx!.currentTime, 0.3);
    this.heartRate = 0.85 - this.dread * 0.42; // seconds per beat

    this.heartTimer -= dt;
    if (this.heartTimer <= 0) {
      this.heartTimer = this.heartRate;
      this.thump(0.0, 1.0);
      this.thump(0.16, 0.62); // the second, softer beat of a lub-dub
    }
  }

  private thump(delay: number, strength: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime + delay;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(62, t);
    osc.frequency.exponentialRampToValueAtTime(34, t + 0.11);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(strength, t + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

    osc.connect(gain).connect(this.heartGain);
    osc.start(t);
    osc.stop(t + 0.25);
  }

  private updateBreathing(dt: number): void {
    const intensity = Math.max(this.exertion, this.dread * 0.55);
    if (intensity < 0.1) {
      this.breathGain.gain.setTargetAtTime(0, this.ctx!.currentTime, 0.5);
      return;
    }

    this.breathGain.gain.setTargetAtTime(0.3 * intensity, this.ctx!.currentTime, 0.3);
    this.breathRate = 3.4 - intensity * 2.3;

    this.breathTimer -= dt;
    if (this.breathTimer <= 0) {
      this.breathTimer = this.breathRate;
      this.breath(intensity);
    }
  }

  private breath(intensity: number): void {
    const ctx = this.ctx!;
    const t = ctx.currentTime;
    const duration = 0.45 + (1 - intensity) * 0.3;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(duration + 0.1);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(520, t);
    filter.frequency.linearRampToValueAtTime(900, t + duration * 0.6);
    filter.Q.value = 0.9;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.9, t + duration * 0.35);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    source.connect(filter).connect(gain).connect(this.breathGain);
    source.start(t);
    source.stop(t + duration + 0.1);
  }

  /** A sharp involuntary gasp — the failure state of holding your breath. */
  gasp(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(0.5);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(1300, t);
    filter.frequency.exponentialRampToValueAtTime(430, t + 0.35);
    filter.Q.value = 1.4;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(1.1, t + 0.05);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.42);

    source.connect(filter).connect(gain).connect(this.master);
    source.start(t);
    source.stop(t + 0.5);
  }

  // -------------------------------------------------------------------------
  // Positional cues
  // -------------------------------------------------------------------------

  /**
   * Build a spatialised channel for a one-shot at a world position.
   *
   * The manual lowpass is the important part: air and foliage swallow high
   * frequencies over distance, so a far-away stomp must be *dull* as well as
   * quiet. Without this everything sounds like it is happening next to you,
   * only softer, and players lose all ability to judge range.
   */
  private spatial(
    x: number,
    y: number,
    z: number,
    opts: { reverb?: number; rolloff?: number; maxCutoff?: number } = {},
  ): { input: GainNode; distance: number } {
    const ctx = this.ctx!;

    const dx = x - this.listenerPos.x;
    const dy = y - this.listenerPos.y;
    const dz = z - this.listenerPos.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = 6;
    panner.maxDistance = 400;
    panner.rolloffFactor = opts.rolloff ?? 0.9;
    panner.positionX.value = x;
    panner.positionY.value = y;
    panner.positionZ.value = z;

    const air = ctx.createBiquadFilter();
    air.type = 'lowpass';
    const maxCutoff = opts.maxCutoff ?? 14000;
    // Full brightness up close, collapsing toward a dull thud at range.
    air.frequency.value = Math.max(180, maxCutoff * Math.exp(-distance / 42));

    const input = ctx.createGain();
    input.connect(air).connect(panner);
    panner.connect(this.master);

    // Distant sounds are proportionally wetter — that is what "far away in a
    // big space" sounds like.
    const wet = ctx.createGain();
    wet.gain.value = (opts.reverb ?? 0.35) * Math.min(1, 0.25 + distance / 90);
    panner.connect(wet).connect(this.reverbSend);

    return { input, distance };
  }

  /**
   * Raw context access, for systems that build their own nodes (voice chat
   * needs MediaStream sources and analysers). Null until start().
   */
  get context(): AudioContext | null {
    return this.ctx;
  }

  /**
   * Spatialised channel for a live voice stream.
   *
   * Same acoustic model as game cues — HRTF panning, air absorption, distance
   * reverb — so a teammate's voice obeys the same physics as a stomp and can
   * be located by ear the same way. The falloff is much steeper than a cue's,
   * though: intelligible speech at long range would shrink the forest.
   *
   * Position updates are smoothed with setTargetAtTime rather than jumped,
   * because network positions arrive in steps and a stepping HRTF pan crackles.
   */
  createVoiceChannel(): VoiceChannel | null {
    const ctx = this.ctx;
    if (!ctx) return null;

    const panner = ctx.createPanner();
    panner.panningModel = 'HRTF';
    panner.distanceModel = 'inverse';
    panner.refDistance = VOICE.REF_DISTANCE;
    panner.maxDistance = 400;
    panner.rolloffFactor = VOICE.ROLLOFF;

    const air = ctx.createBiquadFilter();
    air.type = 'lowpass';
    air.frequency.value = VOICE.MAX_CUTOFF;

    const input = ctx.createGain();
    input.connect(air).connect(panner);
    panner.connect(this.master);

    const wet = ctx.createGain();
    wet.gain.value = 0.18;
    panner.connect(wet).connect(this.reverbSend);

    const setPosition = (x: number, y: number, z: number): void => {
      const t = ctx.currentTime;
      const smooth = 0.08;
      panner.positionX.setTargetAtTime(x, t, smooth);
      panner.positionY.setTargetAtTime(y, t, smooth);
      panner.positionZ.setTargetAtTime(z, t, smooth);

      // Air absorption and reverb wetness track distance continuously, the
      // same curves spatial() applies once per one-shot.
      const dx = x - this.listenerPos.x;
      const dy = y - this.listenerPos.y;
      const dz = z - this.listenerPos.z;
      const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
      air.frequency.setTargetAtTime(
        Math.max(300, VOICE.MAX_CUTOFF * Math.exp(-distance / 42)),
        t,
        0.15,
      );
      wet.gain.setTargetAtTime(0.18 * Math.min(1, 0.25 + distance / 90), t, 0.25);
    };

    const dispose = (): void => {
      input.disconnect();
      air.disconnect();
      panner.disconnect();
      wet.disconnect();
    };

    return { input, setPosition, dispose };
  }

  /** Route a server cue to the right synth. */
  playCue(kind: number, x: number, y: number, z: number, gain: number): void {
    if (!this.ctx) return;

    switch (kind) {
      case CueKind.DistantStomp:
        this.stomp(x, y, z, gain, false);
        break;
      case CueKind.NearStomp:
        this.stomp(x, y, z, gain, true);
        break;
      case CueKind.Siren:
        this.siren(x, y, z, gain, true);
        break;
      case CueKind.DistantSiren:
        this.siren(x, y, z, gain, false);
        break;
      case CueKind.TreeCrash:
        this.treeCrash(x, y, z);
        break;
      case CueKind.Roar:
        this.roar(x, y, z);
        break;
      case CueKind.Screech:
        this.screech(x, y, z, gain);
        break;
      case CueKind.BranchSnap:
        this.branchSnap(x, y, z, gain);
        break;
      case CueKind.Crow:
        this.crow(x, y, z, gain);
        break;
    }
  }

  /**
   * A branch giving way somewhere in the dark.
   *
   * Short, dry and high — nothing like a footfall. That distinction is the
   * whole point: ambience may come from anywhere, so it must never be
   * confusable with a sound that tells you where he is.
   */
  branchSnap(x: number, y: number, z: number, gain: number): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.45, maxCutoff: 13000 });
    const t = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(0.25);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2600, t);
    filter.frequency.exponentialRampToValueAtTime(1100, t + 0.09);
    filter.Q.value = 2.4;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain * 0.7, t + 0.004);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.16);

    source.connect(filter).connect(g).connect(input);
    source.start(t);
    source.stop(t + 0.25);
  }

  /** A crow going up somewhere off in the trees. */
  crow(x: number, y: number, z: number, gain: number): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.65, maxCutoff: 9000 });
    const t = ctx.currentTime;

    // Two or three harsh caws.
    const caws = 2 + (Math.random() < 0.4 ? 1 : 0);
    for (let i = 0; i < caws; i++) {
      const start = t + i * 0.26;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(660, start);
      osc.frequency.exponentialRampToValueAtTime(370, start + 0.16);

      const shaper = ctx.createBiquadFilter();
      shaper.type = 'bandpass';
      shaper.frequency.value = 1500;
      shaper.Q.value = 1.1;

      const g = ctx.createGain();
      g.gain.setValueAtTime(0, start);
      g.gain.linearRampToValueAtTime(gain * 0.34, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, start + 0.2);

      osc.connect(shaper).connect(g).connect(input);
      osc.start(start);
      osc.stop(start + 0.24);
    }
  }

  /** A footfall: sub-bass impact, body thud, and debris. */
  stomp(x: number, y: number, z: number, gain: number, heavy: boolean): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.5, rolloff: 0.85, maxCutoff: 5200 });
    const t = ctx.currentTime;

    // Sub-bass: the part you feel rather than hear.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(heavy ? 74 : 62, t);
    sub.frequency.exponentialRampToValueAtTime(26, t + 0.4);

    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0, t);
    subGain.gain.linearRampToValueAtTime(gain * 1.5, t + 0.015);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 0.55);
    sub.connect(subGain).connect(input);
    sub.start(t);
    sub.stop(t + 0.6);

    // Impact transient: filtered noise crack.
    const crack = ctx.createBufferSource();
    crack.buffer = this.noiseBuffer(0.3);
    const crackFilter = ctx.createBiquadFilter();
    crackFilter.type = 'lowpass';
    crackFilter.frequency.setValueAtTime(heavy ? 2400 : 1500, t);
    crackFilter.frequency.exponentialRampToValueAtTime(220, t + 0.18);

    const crackGain = ctx.createGain();
    crackGain.gain.setValueAtTime(0, t);
    crackGain.gain.linearRampToValueAtTime(gain * 0.5, t + 0.008);
    crackGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);

    crack.connect(crackFilter).connect(crackGain).connect(input);
    crack.start(t);
    crack.stop(t + 0.35);
  }

  /**
   * The siren. Several detuned sawtooth horns sweeping together — the beating
   * between them is what makes the sound feel wrong rather than merely loud.
   */
  siren(x: number, y: number, z: number, gain: number, near: boolean): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.85, rolloff: 0.55, maxCutoff: 9000 });
    const t = ctx.currentTime;
    const duration = near ? 3.4 : 4.6;
    const base = near ? 300 : 250;

    const bus = ctx.createGain();
    bus.gain.setValueAtTime(0, t);
    bus.gain.linearRampToValueAtTime(gain * 0.5, t + 0.5);
    bus.gain.setValueAtTime(gain * 0.5, t + duration - 1.1);
    bus.gain.exponentialRampToValueAtTime(0.001, t + duration);

    /**
     * A raised-cosine wail, sampled densely.
     *
     * The sweep used to be three chained exponentialRampToValueAtTime calls,
     * which have a discontinuous slope where the segments meet. Worse, the
     * band-pass below was fixed while the sawtooth swept *through* it, so each
     * harmonic popped across the passband in turn — that is what made it sound
     * like it was stepping in increments rather than gliding.
     *
     * A raised cosine has zero slope at both ends, so the turn at the top of
     * the wail is smooth rather than a corner.
     */
    const steps = 400;
    const sweep = new Float32Array(steps);
    const filterSweep = new Float32Array(steps);
    const lo = 0.66;
    const hi = 1.62;

    for (let i = 0; i < steps; i++) {
      const u = i / (steps - 1);
      const wail = 0.5 - 0.5 * Math.cos(Math.PI * 2 * u);
      // A slow secondary wobble keeps it from sounding synthetic.
      const wobble = 1 + Math.sin(u * Math.PI * 6) * 0.012;
      const frequency = base * (lo + (hi - lo) * wail) * wobble;

      sweep[i] = frequency;
      // The filter rides the fundamental, so the timbre stays put while the
      // pitch moves. This is the half that actually removes the stepping.
      filterSweep[i] = frequency * 2.4;
    }

    const shaper = ctx.createBiquadFilter();
    shaper.type = 'bandpass';
    shaper.Q.value = 0.7;
    shaper.frequency.setValueCurveAtTime(filterSweep, t, duration);
    bus.connect(shaper).connect(input);

    // Three horns, deliberately out of tune with each other.
    const detunes = [0, 17, -23];
    for (const detune of detunes) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.detune.value = detune;
      osc.frequency.setValueCurveAtTime(sweep, t, duration);

      const oscGain = ctx.createGain();
      oscGain.gain.value = 1 / detunes.length;
      osc.connect(oscGain).connect(bus);
      osc.start(t);
      osc.stop(t + duration + 0.1);
    }

    // A slow tremolo, like a rotating horn. Shallow enough to add movement
    // without reintroducing a pulse of its own.
    const tremolo = ctx.createOscillator();
    tremolo.frequency.value = 3.6;
    const tremoloGain = ctx.createGain();
    tremoloGain.gain.value = 0.16;
    tremolo.connect(tremoloGain).connect(bus.gain);
    tremolo.start(t);
    tremolo.stop(t + duration + 0.1);
  }

  /** Splintering wood as he walks through a trunk. */
  treeCrash(x: number, y: number, z: number): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.6, maxCutoff: 7000 });
    const t = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(1.4);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(2600, t);
    filter.frequency.exponentialRampToValueAtTime(320, t + 1.1);
    filter.Q.value = 0.7;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.85, t + 0.03);
    gain.gain.exponentialRampToValueAtTime(0.06, t + 0.5);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 1.3);

    source.connect(filter).connect(gain).connect(input);
    source.start(t);
    source.stop(t + 1.4);

    // The trunk hitting the ground a moment later.
    window.setTimeout(() => this.stomp(x, y, z, 0.5, false), 900);
  }

  /** Close-range roar for the grab. */
  roar(x: number, y: number, z: number): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.5, rolloff: 0.4, maxCutoff: 11000 });
    const t = ctx.currentTime;
    const duration = 2.2;

    for (const [type, detune, level] of [
      ['sawtooth', 0, 0.5],
      ['square', -14, 0.25],
      ['sawtooth', 25, 0.3],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = type;
      osc.detune.value = detune;
      osc.frequency.setValueAtTime(180, t);
      osc.frequency.exponentialRampToValueAtTime(58, t + duration);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(level, t + 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

      osc.connect(gain).connect(input);
      osc.start(t);
      osc.stop(t + duration + 0.1);
    }

    const noise = ctx.createBufferSource();
    noise.buffer = this.noiseBuffer(duration);
    const noiseFilter = ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.setValueAtTime(900, t);
    noiseFilter.frequency.exponentialRampToValueAtTime(200, t + duration);
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.5, t);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t + duration);
    noise.connect(noiseFilter).connect(noiseGain).connect(input);
    noise.start(t);
    noise.stop(t + duration);
  }

  /** Flare ignition and similar bright, hissing one-shots. */
  screech(x: number, y: number, z: number, gain: number): void {
    const ctx = this.ctx!;
    const { input } = this.spatial(x, y, z, { reverb: 0.3, maxCutoff: 12000 });
    const t = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(1.2);
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 1800;

    const g = ctx.createGain();
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(gain, t + 0.04);
    g.gain.exponentialRampToValueAtTime(0.001, t + 1.0);

    source.connect(filter).connect(g).connect(input);
    source.start(t);
    source.stop(t + 1.2);
  }

  // -------------------------------------------------------------------------
  // Player-local sounds
  // -------------------------------------------------------------------------

  footstep(crouching: boolean, running: boolean): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const level = crouching ? 0.06 : running ? 0.24 : 0.13;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(0.22);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    // Slight random variation stops a walk cycle sounding like a metronome.
    filter.frequency.value = 320 + Math.random() * 260;
    filter.Q.value = 1.1;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(level, t + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.17);

    source.connect(filter).connect(gain).connect(this.master);
    source.start(t);
    source.stop(t + 0.22);
  }

  /**
   * The confrontation sting: a hard non-positional hit for the moment control
   * is taken away.
   *
   * Deliberately not spatialised. Everything else in this game is placed in
   * the world so you can locate it; this one is inside your head, and skipping
   * the panner is what makes it feel like a reaction rather than an event.
   */
  stinger(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const bus = ctx.createGain();
    bus.gain.value = 0.9;
    bus.connect(this.master);
    // A little tail, so it lands in the same space as everything else.
    const wet = ctx.createGain();
    wet.gain.value = 0.5;
    bus.connect(wet).connect(this.reverbSend);

    // A dissonant cluster, rising. The minor second is doing the work.
    for (const [freq, level] of [
      [196, 0.34],
      [208, 0.3],
      [294, 0.22],
      [415, 0.16],
    ] as const) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq * 0.94, t);
      osc.frequency.linearRampToValueAtTime(freq, t + 0.35);

      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(level, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 1.5);

      osc.connect(gain).connect(bus);
      osc.start(t);
      osc.stop(t + 1.6);
    }

    // Sub-bass drop underneath — the part you feel in your chest.
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.setValueAtTime(110, t);
    sub.frequency.exponentialRampToValueAtTime(31, t + 1.1);
    const subGain = ctx.createGain();
    subGain.gain.setValueAtTime(0, t);
    subGain.gain.linearRampToValueAtTime(0.85, t + 0.02);
    subGain.gain.exponentialRampToValueAtTime(0.001, t + 1.3);
    sub.connect(subGain).connect(bus);
    sub.start(t);
    sub.stop(t + 1.4);

    // Noise crack on the transient so it has teeth.
    const crack = ctx.createBufferSource();
    crack.buffer = this.noiseBuffer(0.4);
    const crackFilter = ctx.createBiquadFilter();
    crackFilter.type = 'highpass';
    crackFilter.frequency.value = 2400;
    const crackGain = ctx.createGain();
    crackGain.gain.setValueAtTime(0.5, t);
    crackGain.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    crack.connect(crackFilter).connect(crackGain).connect(bus);
    crack.start(t);
    crack.stop(t + 0.4);
  }

  /** Flashlight switch — a small, satisfying mechanical click. */
  click(): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.setValueAtTime(2200, t);
    osc.frequency.exponentialRampToValueAtTime(600, t + 0.03);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.16, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.05);

    osc.connect(gain).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.06);
  }

  /** Radio static, used by the tower transmit minigame. */
  staticBurst(level: number, duration = 0.25): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const source = ctx.createBufferSource();
    source.buffer = this.noiseBuffer(duration + 0.05);

    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1400;
    filter.Q.value = 0.6;

    const gain = ctx.createGain();
    gain.gain.value = level * 0.25;

    source.connect(filter).connect(gain).connect(this.master);
    source.start(t);
    source.stop(t + duration);
  }

  /** A clean carrier tone — the sound of being on-signal. */
  carrier(level: number, duration = 0.2): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;

    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 1180;

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(level * 0.14, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + duration);

    osc.connect(gain).connect(this.master);
    osc.start(t);
    osc.stop(t + duration + 0.05);
  }
}
